// App shell: the panel grid, the PnL sidebar, connection status, and the wiring from engine events to panels.

import { skeletonHtml, unitHtml } from './fmt';
import type { Engine } from '../core/engine';
import {
  type ConnectionStatus, KNOWN_VENUES, REQUIRED_EXTENSION_VERSION, type VenueState, type VenueStatus, extensionOutdated,
  getConnectionStatus,
} from '../kalshi/connection';
import { formatCash, formatMq } from '../core/money';
import type { AccountRow, PnlSnapshot, PnlTreeNode } from '../core/types';
import { MOBILE_ID } from '../core/accounts';
import type { FillRow } from '../core/db';
import { CELL_RANGE, DEFAULT_CELL, GridLayout, LEGACY_CELL, cellPx, scaleRect, type GridRect } from './grid';
import { FONTS, PANEL_MODES, THEMES, getDefaultPanelMode, getFont, getTheme,
  setDefaultPanelMode, setFont, setTheme } from './theme';
import { clearLog, logLines, onLog, type LogLine } from '../core/log';
import { SHARD_NAMES, type ShardAllocation, type ShardBalance } from '../kalshi/client';
import { clearJournal, exportJournal, journalEntries, loadJournal, onJournal, type JournalEntry } from '../core/journal';
import { Panel, type PanelHost, type PanelMode } from './panel';
import type { PmAccount } from '../polymarket/feed';
import { PolymarketEngine } from '../polymarket/engine';
import type { RealVenueId, VenueEngine, VenueId } from '../venue/engine';
import { ArbEngine } from '../arb/engine';
import { type ArbLegsPartial, ArbPanel } from './arbpanel';
import { escHtml, setBanner, toast } from './toast';
import { mountWatchlistPreview } from '../watchlist-preview'; // watchlist preview

const LAYOUT_KEY = 'kg_layout_v1';
/** Cell size the saved layout was laid out at; absent means the old fixed 64px. */
const LAYOUT_CELL_KEY = 'kg_layout_cell';
const GRID_CELL_KEY = 'kg_grid_cell';

/** Fills rendered per account before a "show more". */
const FILLS_PAGE = 25;

// Bracket hedges are posted by this tab; a tab that dies with an entry resting leaves it unhedged.
const DEADMAN_HELP =
  'Bracket orders are worked by this tab: when the entry fills, this page posts the hedge.\n\n'
  + 'ON — if the tab is closed or crashes with a bracket armed, the extension cancels the '
  + 'resting entry orders, so you cannot end up filled with no hedge coming. It signs the '
  + 'cancels at that moment, so they are still valid.\n\n'
  + 'OFF — entries are left resting. Only useful if you want them to survive a reload, and '
  + 'only safe if you are watching them.';

// Adding a view is one entry here plus a matching `<div class="sb-view" data-view="…">` in the markup.
interface SidebarView {
  id: string;
  title: string;
  icon: string;
  /** Bottom cluster — profile/logs/settings, pinned below the spacer. */
  bottom?: boolean;
  /** Toggles the bottom drawer instead of switching the side panel. */
  panel?: boolean;
  /** A venue's own view: its rail icon exists only while that venue has a key loaded. */
  venue?: string;
  /** Venue icons: x of the status dot, just past the letter's right edge like a full stop. */
  dotX?: number;
}

const ICON = {
  pnl: '<path d="M2 13V3M2 13h12"/><path d="M4.4 10.4l2.9-2.9 2.4 2.4 3.4-3.9"/>',
  watchlist: '<path d="M4 2.5h8v11L8 10.7 4 13.5z"/>', // watchlist preview
  // Venue glyphs are bold letters, not stroke icons, to avoid a logo argument.
  kalshi: '<text x="8" y="12.2" text-anchor="middle" font-size="12.5" font-weight="800" fill="currentColor" stroke="none">K</text>',
  polymarket: '<text x="8" y="12.2" text-anchor="middle" font-size="12.5" font-weight="800" fill="currentColor" stroke="none">P</text>',
  profile: '<circle cx="8" cy="5.6" r="2.5"/><path d="M3 14a5 5 0 0 1 10 0"/>',
  logs: '<path d="M2.2 3.4h11.6M2.2 6.6h11.6M2.2 9.8h7.4M2.2 13h5.2"/>',
  ext: '<path d="M6.3 2.6a1.5 1.5 0 0 1 3 0V4h2.4a.9.9 0 0 1 .9.9v2.3h1.1a1.5 1.5 0 0 1 0 3h-1.1v2.4'
    + 'a.9.9 0 0 1-.9.9H9.3a1.5 1.5 0 0 0-3 0H3.9a.9.9 0 0 1-.9-.9v-2.4h1.1a1.5 1.5 0 0 0 0-3H3V4.9'
    + 'A.9.9 0 0 1 3.9 4h2.4z"/>',
  settings: '<circle cx="8" cy="8" r="2.15"/>'
    + '<path d="M12.9 8c0-.3-.03-.6-.08-.88l1.28-1-1.5-2.6-1.52.6a4.9 4.9 0 0 0-1.52-.88L9.36 1.6h-3l-.2 1.64c-.55.2-1.06.5-1.52.88l-1.52-.6-1.5 2.6 1.28 1A5 5 0 0 0 3.1 8c0 .3.03.6.08.88l-1.28 1 1.5 2.6 1.52-.6c.46.38.97.68 1.52.88l.2 1.64h3l.2-1.64c.55-.2 1.06-.5 1.52-.88l1.52.6 1.5-2.6-1.28-1c.05-.28.08-.58.08-.88z"/>',
};

const VIEWS: SidebarView[] = [
  { id: 'pnl', title: 'Consolidated', icon: ICON.pnl },
  { id: 'kalshi', title: 'Kalshi', icon: ICON.kalshi, venue: 'kalshi', dotX: 14.55 },
  { id: 'polymarket', title: 'Polymarket US', icon: ICON.polymarket, venue: 'polymarket', dotX: 14.2 },
  { id: 'watchlist', title: 'Watchlist', icon: ICON.watchlist }, // watchlist preview
  { id: 'profile', title: 'Profile', icon: ICON.profile, bottom: true },
  { id: 'logs', title: 'Logs', icon: ICON.logs, bottom: true, panel: true },
  { id: 'extension', title: 'Extension', icon: ICON.ext, bottom: true },
  { id: 'settings', title: 'Settings', icon: ICON.settings, bottom: true },
];

// Remote-indicator chevrons; the down-slash is short and lives in the gap between them.
const REMOTE_SVG = (down: boolean) => `<svg viewBox="0 0 16 16" width="14" height="14" fill="none"
  stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
  <path d="M2 4L4.8 8L2 12"/><path d="M14 4L11.2 8L14 12"/>
  ${down ? '<path d="M9.3 4.5L6.7 11.5"/>' : ''}</svg>`;

const SIDEBAR_KEY = 'kg_sidebar_v1';

function venueDotClass(state: VenueState): string {
  return state === 'connected' ? 'ok' : state === 'disconnected' ? 'bad' : state === 'checking' ? 'warn' : '';
}

function venueStateLabel(state: VenueState): string {
  return state === 'unset' ? 'not set up' : state === 'checking' ? 'checking…' : state;
}
const DEADMAN_KEY = 'kg_deadman';
const LOGPANEL_KEY = 'kg_logpanel_v1';

interface SavedPanel {
  id: string;
  rect: GridRect;
  ticker: string | null;
  mode?: PanelMode;
  /** Absent means Kalshi — every layout saved before venues existed. */
  venue?: 'kalshi' | 'polymarket' | 'arb';
  /** Arb ladders only: their two legs. */
  legs?: ArbLegsPartial;
  /** Arb ladders only: whether clicks may cross. Absent means on, as before the switch existed. */
  cross?: boolean;
}

export class App {
  /** Every single-venue ladder; `p.venue` says which engine feeds it. */
  private panels: Panel[] = [];
  /** Arb ladders: two legs each, fed by both engines. */
  private arbs: ArbPanel[] = [];
  private grid!: GridLayout;
  private accountsList: { id: string; name: string }[] = [];
  /** Per-venue collapse / paging state for the account and fill lists, so the two views don't share it. */
  private listState: Record<'kalshi' | 'polymarket', { acct: Map<string, boolean>; acctInit: boolean; fills: Map<string, boolean>; shown: Map<string, number> }> = {
    kalshi: { acct: new Map(), acctInit: false, fills: new Map(), shown: new Map() },
    polymarket: { acct: new Map(), acctInit: false, fills: new Map(), shown: new Map() },
  };
  private fillsEdit = false;
  private lastPnl: PnlSnapshot | null = null;
  /** Polymarket's P&L in Kalshi's shape; its total is the one both the venue view and Consolidated show. */
  private lastPmPnl: PnlSnapshot | null = null;
  /** Polymarket's two sockets, for the P icon's tooltip. */
  private pmFeedText = '';
  private venues: VenueStatus[] = [];
  /** Accounts are the app's, not per-venue: creating one always goes to the Kalshi engine. */
  private pmEngine = new PolymarketEngine({ createAccount: (name) => this.engine.createAccount(name) });
  /** Arb pairs as a venue: subscribes both legs on their engines, emits the synthetic book. */
  private arbEngine!: ArbEngine;
  private wsHost = '';
  private logOpen = false;
  private logHeight = 210;
  private activeView = VIEWS[0].id;
  private sidebarOpen = true;

  constructor(private root: HTMLElement, private engine: Engine) {
    this.arbEngine = new ArbEngine((v) => this.engineFor(v), [this.engine, this.pmEngine]);
    this.arbEngine.onPhantomChange(() => { for (const p of this.panels) if (p.venue !== 'arb') p.refresh(); });
    // An arb alarm is money at risk on one leg: toast now, banner until the exposure is gone.
    this.arbEngine.onAlarm((a) => { toast(a.msg, a.level); this.renderArbBanner(); });
    this.arbEngine.on('snapshot', () => this.renderArbBanner());
  }

  /** Unhedged contracts on any quoter leg, as a banner that stays until they are flat. */
  private renderArbBanner(): void {
    const exp = this.arbEngine.exposure();
    if (!exp.length) {
      setBanner('kg-arb-banner', null);
      return;
    }
    const items = exp.map((x) => `${x.unhedgedMq / 1000} ${x.side === 'bid' ? 'long' : 'short'} ${x.leg.ticker} (${x.orderId})`).join(' · ');
    setBanner('kg-arb-banner', `ARB UNHEDGED — ${items} — flatten by hand, then cancel the arb row`);
  }

  mount(): void {
    this.root.innerHTML = `
<div class="topbar">
  <span class="tb-title">KALSHI</span>
  <span class="tb-spacer"></span>
  <div class="tb-remote" id="wsChip">
    <span id="wsInfo">Connecting…</span>
    <span class="tb-remote-ic" id="wsIcon"></span>
    <div class="tb-pop">
      <div class="tb-pop-hd" id="popState">Connecting…</div>
      <div class="tb-pop-row"><span>Feed</span><span class="mono" id="popHost">—</span></div>
      <div class="tb-pop-row"><span>Streaming</span><span class="mono" id="popSubs">—</span></div>
      <div class="tb-pop-row" id="popDetailRow"><span>Problem</span><span class="mono" id="popDetail">—</span></div>
      <div class="tb-pop-note" id="popNote"></div>
      <div class="tb-pop-sep"></div>
      <div id="popSteps"></div>
    </div>
  </div>
</div>
<div class="main-layout">
  <div class="grid-area">
    <div id="grid"></div>
    <div class="grid-empty" id="gridEmpty">
      <div class="ge-card">
        <div class="ge-mark"><svg viewBox="0 0 34 26" width="34" height="26" fill="none"
          stroke="currentColor" stroke-width="1.4" stroke-linejoin="round">
          <rect x="1" y="1" width="32" height="24" rx="2"/><path d="M1 7h32"/></svg></div>
        <div class="ge-title">No ladders open</div>
        <div class="ge-sub">Add one with the <b>+</b> button.<br/>Drag a ladder by its bar to move it, by its corner to resize.</div>
      </div>
    </div>
    <div class="fab-wrap" id="fabWrap">
      <div class="fab-menu" id="fabMenu" role="menu">
        ${KNOWN_VENUES.map((v) => `<button class="fab-item" data-venue="${v.id}" title="Add a ${escHtml(v.label)} ladder"${v.id === 'kalshi' ? '' : ' style="display:none"'}><span class="fab-plus">+</span>${escHtml(v.label)}<span class="fab-sub">ladder</span></button>`).join('')}
        <button class="fab-item" data-venue="arb" title="Add an arb ladder: a quoter leg against a hedger leg"><span class="fab-plus">+</span>Arb<span class="fab-sub">quoter − hedger</span></button>
      </div>
      <button class="fab" id="addBtn" title="Add a ladder — hover for venues">+</button>
    </div>
  </div>
  <div class="sidebar" id="sidebar">
    <div class="sb-head">
      <span class="sb-title" id="sbTitle">Account</span>
      <button class="sb-x" id="sbCollapse" title="Collapse sidebar">&#10095;</button>
    </div>
    <div class="sb-body">

      <div class="sb-view" data-view="pnl">
        <div class="pnl-hdr">All venues</div>
        <div class="pnl-row"><span class="pnl-lbl">Balance</span><span class="pnl-val" id="accBalance">—</span></div>
        <div class="pnl-row"><span class="pnl-lbl">Total P&amp;L</span><span class="pnl-val" id="accTotalPnl">—</span></div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr">By venue</div>
        <div id="consolidatedByVenue"></div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr">Starred</div>
        <div id="pnlStarred"></div>
      </div>

      <div class="sb-view" data-view="kalshi">
        <div class="pnl-row"><span class="pnl-lbl">Balance</span><span class="pnl-val" id="kBalance">—</span></div>
        <div class="pnl-row"><span class="pnl-lbl">P&amp;L</span><span class="pnl-val" id="kTotalPnl">—</span></div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr">Accounts</div>
        <div id="pnlPositions"></div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr-row">
          <span class="pnl-hdr" style="margin-bottom:0">Fills by Account</span>
          <span id="fillsEditToggle" class="pnl-toggle-btn">edit</span>
        </div>
        <div id="fillsEditBar" class="fills-editbar" style="display:none">
          <span class="fills-hint">Drag a fill onto another account.</span>
          <span>
            <span id="fillsExport" class="pnl-toggle-btn">export</span>
            <span id="fillsImport" class="pnl-toggle-btn">import</span>
          </span>
        </div>
        <div id="pnlFillsByAcct"></div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr">Orders Out</div>
        <div id="pnlOrdersOut"></div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr">Past 24h</div>
        <div id="pnlRecent"></div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr-row">
          <span class="pnl-hdr" style="margin-bottom:0">Past Trades</span>
          <span id="pnlSettledToggle" class="pnl-toggle-btn">show</span>
        </div>
        <div id="pnlSettled" style="display:none"></div>
      </div>

      <!-- Polymarket US: its rail icon and this view exist only while its key is loaded. Same sections as Kalshi's. -->
      <div class="sb-view" data-view="polymarket">
        <div class="pnl-row"><span class="pnl-lbl">Balance</span><span class="pnl-val" id="pmBalance">—</span></div>
        <div class="pnl-row"><span class="pnl-lbl">Buying power</span><span class="pnl-val" id="pmBuyingPower">—</span></div>
        <div class="pnl-row"><span class="pnl-lbl">P&amp;L</span><span class="pnl-val" id="pmTotalPnl">—</span></div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr">Accounts</div>
        <div id="pmAccounts"></div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr">Fills by Account</div>
        <div id="pmFillsByAcct"></div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr">Orders Out</div>
        <div id="pmOrdersOut"></div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr">Past 24h</div>
        <div id="pmRecent"></div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr-row">
          <span class="pnl-hdr" style="margin-bottom:0">Past Trades</span>
          <span id="pmSettledToggle" class="pnl-toggle-btn">show</span>
        </div>
        <div id="pmSettled" style="display:none"></div>
      </div>

      <div class="sb-view" data-view="watchlist" id="wlView"></div><!-- watchlist preview -->

      <div class="sb-view" data-view="profile">
        <div class="ph-badge">Placeholder — nothing here is wired up</div>
        <div class="prof-card">
          <div class="prof-avatar">J</div>
          <div style="min-width:0">
            <div class="prof-name">Trader</div>
            <div class="prof-sub mono" id="profKey">—</div>
          </div>
        </div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr">Session</div>
        <div class="pnl-row"><span class="pnl-lbl">Environment</span><span class="pnl-val mono" id="profEnv" style="font-size:11px">—</span></div>
        <div class="pnl-row"><span class="pnl-lbl">Origin</span><span class="pnl-val mono" id="profOrigin" style="font-size:11px">—</span></div>
        <div class="pnl-row"><span class="pnl-lbl">Trading</span><span class="pnl-val mono" id="profTrading" style="font-size:11px">—</span></div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr">Venues</div>
        <div id="profVenues"></div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr">Account</div>
        <button class="ph-btn" disabled>Switch sub-account…</button>
        <button class="ph-btn" disabled>Export ledger</button>
        <button class="ph-btn" disabled>Forget this device</button>
      </div>

      <div class="sb-view" data-view="extension">
        <div class="pnl-hdr">Browser extension</div>
        <a class="ext-btn" href="/kalshi-extension.zip" download>Download latest (.zip)</a>
        <div class="pnl-row"><span class="pnl-lbl">Installed</span><span class="pnl-val mono" id="extVer" style="font-size:11px">—</span></div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr">Install</div>
        <ol class="ext-steps">
          <li>Open <code>chrome://extensions</code>, turn on <b>Developer mode</b>.</li>
          <li><b>Load unpacked</b> → pick the unzipped folder.</li>
          <li>Open its <b>Details</b> → set <b>Site access: On all sites</b>.</li>
          <li>Click the extension icon, load your API key, approve this page.</li>
        </ol>
        <div class="ext-note">Updating: download, replace the folder's contents, then hit ↻ on the
          card at <code>chrome://extensions</code>. Chrome loads from disk, so a new zip does nothing
          until you replace the files and reload.</div>
      </div>

      <div class="sb-view" data-view="settings">
        <div class="pnl-hdr">Appearance</div>
        <div class="set-row">
          <label class="pnl-lbl" for="setTheme">Theme</label>
          <select class="set-in" id="setTheme"></select>
        </div>
        <div class="set-row">
          <label class="pnl-lbl" for="setFont">Typeface</label>
          <select class="set-in" id="setFont"></select>
        </div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr">Workspace</div>
        <div class="set-row">
          <label class="pnl-lbl" for="setPanelMode">New ladders open in</label>
          <select class="set-in" id="setPanelMode"></select>
        </div>
        <div class="set-row">
          <label class="pnl-lbl" for="setGridCell">Grid size</label>
          <span class="set-range"><input type="range" id="setGridCell" min="${CELL_RANGE.min}" max="${CELL_RANGE.max}" step="1"/><span class="mono" id="setGridCellVal"></span></span>
        </div>
        <div class="pnl-divider"></div>
        <div class="pnl-hdr">Safety</div>
        <label class="tb-switch" id="deadmanWrap" title="${escHtml(DEADMAN_HELP)}">
          <input type="checkbox" id="deadmanChk" checked/>
          <span class="tb-track"><span class="tb-knob"></span></span>
          <span class="tb-switch-lbl">Cancel entries if this tab closes</span>
        </label>
        <div class="set-note">Bracket entries are cancelled by the extension if this tab dies. Off means they keep resting with no hedge coming.</div>
      </div>

    </div>
    <div class="sb-foot" id="sbFoot">
      <button class="sb-foot-btn" id="shardsBtn"
        title="Kalshi holds collateral per exchange shard: an order on a sports market can only use shard 3's balance. Move money between shards here.">Exchange shards</button>
      <button class="sb-foot-btn" id="rebuildBtn"
        title="Re-pull the whole fill history from Kalshi and re-derive every position from it. The local ledger is a cache, not the record — this rebuilds it.">Rebuild ledger</button>
    </div>
  </div>
  <div class="activitybar" id="activityBar"></div>
</div>
<div class="logpanel" id="logPanel" style="display:none">
  <div class="lp-drag" id="logDrag" title="Drag to resize"></div>
  <div class="lp-head">
    <span class="lp-title">Logs</span>
    <span class="lp-count" id="logCount">0</span>
    <button class="lp-btn lp-tab" id="logTabLog" title="Everything the app just did, last 400 lines">Activity</button>
    <button class="lp-btn lp-tab" id="logTabJournal" title="Every order, cancel, fill and arb decision, with full request and response — kept on disk">Journal</button>
    <span class="tb-spacer"></span>
    <button class="lp-btn" id="logExport" title="Download the whole journal as newline-delimited JSON">Export</button>
    <button class="lp-btn" id="logClear">Clear</button>
    <button class="lp-btn" id="logClose" title="Hide">&#10005;</button>
  </div>
  <div class="lp-body" id="logBody"></div>
</div>`;

    const noRoom = () => toast('No room left in the grid — close or shrink a ladder first.');
    // The circle itself still adds a Kalshi ladder; the hover menu is where other venues live.
    this.byId('addBtn').addEventListener('click', () => { if (!this.addPanel()) noRoom(); });
    for (const b of document.querySelectorAll<HTMLElement>('.fab-item[data-venue]')) {
      b.addEventListener('click', () => {
        const ok = b.dataset.venue === 'arb' ? this.addArbPanel() : this.addPanel(undefined, b.dataset.venue as VenueId);
        if (!ok) noRoom();
      });
    }
    this.byId('rebuildBtn').addEventListener('click', () => void this.rebuild());

    this.byId('shardsBtn').addEventListener('click', () => void this.toggleShardPop());
    // Persisted: defaults on, but a saved choice (either way) survives reload.
    const deadmanChk = this.byId<HTMLInputElement>('deadmanChk');
    const savedDeadman = localStorage.getItem(DEADMAN_KEY) !== 'off';
    this.engine.setDeadmanEnabled(savedDeadman);
    deadmanChk.checked = savedDeadman;
    deadmanChk.addEventListener('change', () => {
      this.engine.setDeadmanEnabled(deadmanChk.checked);
      localStorage.setItem(DEADMAN_KEY, deadmanChk.checked ? 'on' : 'off');
    });
    for (const [btn, box] of [['pnlSettledToggle', 'pnlSettled'], ['pmSettledToggle', 'pmSettled']]) {
      this.byId(btn).addEventListener('click', (ev) => {
        const el = this.byId(box);
        const hidden = el.style.display === 'none';
        el.style.display = hidden ? '' : 'none';
        (ev.currentTarget as HTMLElement).textContent = hidden ? 'hide' : 'show';
      });
    }

    this.buildSidebar();
    mountWatchlistPreview(this.byId('wlView'), (t) => this.engine.client.getMarket(t)); // watchlist preview
    this.buildSettings();
    this.buildLogPanel();

    this.accountsList = this.engine.accounts.listLeaves();
    this.grid = new GridLayout(this.byId('grid'), {
      onRemove: (id) => this.confirmRemove(id),
      onResized: (id) => (this.panels.find((p) => p.id === id) ?? this.arbs.find((a) => a.id === id))?.onResized(),
      onChange: () => this.saveLayout(),
    });
    this.grid.setCell(Number(localStorage.getItem(GRID_CELL_KEY)) || DEFAULT_CELL);
    this.wireGridSize();
    this.wireEngine();
    this.pmEngine.feed.on('account', (a) => this.renderPolymarket(a));
    const feedState = () => {
      const f = this.pmEngine.feed;
      const word = (ws: string) => (ws === 'open' ? 'live' : ws);
      this.pmFeedText = `Feed: book ${word(f.wsStatus)} · account ${word(f.privateWsStatus)}`;
      const pm = this.venues.find((v) => v.id === 'polymarket');
      if (pm) this.titleVenueIcon(pm);
    };
    this.pmEngine.feed.on('status', feedState);
    this.pmEngine.feed.on('privateStatus', feedState);
    this.restoreLayout();
    this.guardMultipleTabs();
    this.wireUnload();
    this.wireFillsByAccount();
    this.watchExtension();
  }

  /** Polls the extension popup's kill-switch/approval state, which can change under us. */
  private watchExtension(): void {
    const poll = async () => {
      const s = await getConnectionStatus();
      if (s.wsUrl) this.wsHost = new URL(s.wsUrl).host;
      const outdated = extensionOutdated(s.version);
      // Connection state lives in the corner chip, not a page-wide banner, before you're connected.
      if (!this.engine.isStarted) {
        setBanner('kg-ext-banner', null);
      } else if (!s.workerReachable) {
        setBanner('kg-ext-banner', 'Extension not responding — reload it at chrome://extensions, then reload this page');
      } else if (!s.authorized) {
        setBanner('kg-ext-banner', 'This page is no longer approved in the extension — open the extension and approve it');
      } else if (!s.hasKey) {
        setBanner('kg-ext-banner', 'No key loaded — open the extension popup and load your Kalshi key');
      } else if (!s.trading) {
        setBanner('kg-ext-banner', 'Trading is switched OFF in the extension — orders and cancels are blocked', 'warn');
      } else if (outdated) {
        setBanner('kg-ext-banner', `Extension v${s.version} is older than this page expects (v${REQUIRED_EXTENSION_VERSION}) — download it from the Extension tab, replace the folder, reload it`, 'warn');
      } else {
        setBanner('kg-ext-banner', null);
      }
      this.byId('profKey').textContent = s.hasKey ? `${s.keyIdPrefix}…` : 'no key loaded';
      this.byId('profEnv').textContent = s.env ?? '—';
      this.byId('profOrigin').textContent = s.authorized ? window.location.origin : 'not approved';
      this.byId('profTrading').textContent = s.trading ? 'enabled' : 'OFF';
      this.byId('extVer').textContent = s.version
        ? `v${s.version}${outdated ? ` — update to v${REQUIRED_EXTENSION_VERSION}` : ''}`
        : 'not detected';
      this.byId('extVer').style.color = outdated ? 'var(--warn)' : '';
      this.renderVenues(s);

      // Lights, labels only; what to do about a red one lives in the Extension tab.
      // One extension light covers reachable, current and this page approved; its label names whichever failed.
      const extOk = s.bridged && s.workerReachable && !outdated && s.authorized;
      const extLabel = !(s.bridged && s.workerReachable) ? 'Extension not responding'
        : outdated ? `Extension v${s.version} — page needs v${REQUIRED_EXTENSION_VERSION}`
          : !s.authorized ? 'Extension — page not approved'
            : `Extension${s.version ? ` v${s.version}` : ''}`;
      const lights: [boolean, string][] = [[extOk, extLabel]];
      for (const v of s.venues) {
        if (v.id === 'kalshi') lights.push([s.hasKey && v.state === 'connected', s.hasKey ? `${v.label} ${venueStateLabel(v.state)}` : `${v.label} — no key`]);
        else if (v.hasKey) lights.push([v.state === 'connected', `${v.label} ${venueStateLabel(v.state)}`]);
      }
      // An extension that predates the venue list still reports its Kalshi key.
      if (!s.venues.some((v) => v.id === 'kalshi')) lights.splice(1, 0, [s.hasKey, s.hasKey ? 'Kalshi key loaded' : 'Kalshi — no key']);
      lights.push([s.trading, s.trading ? 'Trading on' : 'Trading off']);
      this.byId('popSteps').innerHTML = lights.map(([ok, label]) => `
        <div class="tb-step"><span class="tb-dot ${ok ? 'ok' : 'bad'}"></span>
          <span class="tb-step-t">${escHtml(label)}</span></div>`).join('');

      // Before the engine starts there's no socket to report on, so drive the chip from here instead.
      if (!this.engine.isStarted) {
        const missing = lights.find(([ok]) => !ok)?.[1];
        this.renderStatus('closed', missing ? `waiting for: ${missing.toLowerCase()}` : 'starting…', true);
      }

      setTimeout(poll, 4000);
    };
    void poll();
  }

  private buildSidebar(): void {
    const bar = this.byId('activityBar');
    const btn = (v: SidebarView) => {
      const b = document.createElement('button');
      b.className = 'ab-btn';
      b.dataset.view = v.id;
      b.title = v.title;
      if (v.venue) {
        b.dataset.venue = v.venue;
        if (v.venue !== 'kalshi') b.style.display = 'none'; // until its key is seen
      }
      b.innerHTML = `<svg viewBox="0 0 16 16" width="17" height="17" fill="none" stroke="currentColor"
        stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round">${v.icon}${v.venue
          ? `<circle class="ab-dot" cx="${v.dotX ?? 14.4}" cy="10.85" r="1.35" stroke="none"/>` : ''}</svg>`;
      b.addEventListener('click', () => this.selectView(v.id));
      return b;
    };
    for (const v of VIEWS.filter((x) => !x.bottom)) bar.appendChild(btn(v));
    const spacer = document.createElement('div');
    spacer.className = 'ab-spacer';
    bar.appendChild(spacer);
    for (const v of VIEWS.filter((x) => x.bottom)) bar.appendChild(btn(v));

    this.byId('sbCollapse').addEventListener('click', () => this.setSidebar(false));

    try {
      const saved = JSON.parse(localStorage.getItem(SIDEBAR_KEY) || '{}');
      if (VIEWS.some((v) => v.id === saved.view)) this.activeView = saved.view;
      if (typeof saved.open === 'boolean') this.sidebarOpen = saved.open;
    } catch { /* first run */ }
    this.applySidebar();
  }

  private buildSettings(): void {
    const wire = (id: string, list: { id: string; name: string }[], get: () => string, set: (v: string) => void) => {
      const el = this.byId<HTMLSelectElement>(id);
      for (const c of list) el.add(new Option(c.name, c.id));
      el.value = get();
      el.addEventListener('change', () => set(el.value));
    };
    wire('setTheme', THEMES, getTheme, setTheme);
    wire('setFont', FONTS, getFont, setFont);
    wire('setPanelMode', PANEL_MODES, getDefaultPanelMode, setDefaultPanelMode);
  }

  /** Clicking the icon of the view already showing collapses it, as in VS Code. */
  private selectView(id: string): void {
    const v = VIEWS.find((x) => x.id === id);
    if (v?.panel) {
      this.setLogPanel(!this.logOpen);
      return;
    }
    if (this.sidebarOpen && this.activeView === id) {
      this.setSidebar(false);
      return;
    }
    this.activeView = id;
    this.setSidebar(true);
  }

  private setSidebar(open: boolean): void {
    this.sidebarOpen = open;
    this.applySidebar();
  }

  private applySidebar(): void {
    const v = VIEWS.find((x) => x.id === this.activeView) ?? VIEWS[0];
    this.byId('sidebar').classList.toggle('collapsed', !this.sidebarOpen);
    this.byId('sbTitle').textContent = v.title;
    for (const el of document.querySelectorAll<HTMLElement>('.sb-view')) {
      el.style.display = el.dataset.view === v.id ? '' : 'none';
    }
    this.byId('sbFoot').style.display = v.id === 'kalshi' ? '' : 'none';
    for (const b of document.querySelectorAll<HTMLElement>('.ab-btn')) {
      const bv = VIEWS.find((x) => x.id === b.dataset.view);
      b.classList.toggle('on', bv?.panel
        ? this.logOpen
        : this.sidebarOpen && b.dataset.view === v.id);
    }
    localStorage.setItem(SIDEBAR_KEY, JSON.stringify({ view: v.id, open: this.sidebarOpen }));

    // Sidebar toggle changes workspace width without a window resize; reflow, and remeasure the FAB gutter.
    requestAnimationFrame(() => {
      this.grid?.reflow();
      const right = this.byId('grid').getBoundingClientRect().right;
      document.documentElement.style.setProperty('--sb-w', `${Math.round(window.innerWidth - right)}px`);
    });
  }

  /** A bottom drawer rather than a side view: log lines are long and wrap badly at sidebar width. */
  private buildLogPanel(): void {
    try {
      const saved = JSON.parse(localStorage.getItem(LOGPANEL_KEY) || '{}');
      if (typeof saved.open === 'boolean') this.logOpen = saved.open;
      if (Number.isFinite(saved.h)) this.logHeight = clampHeight(saved.h);
    } catch { /* first run */ }

    this.byId('logClose').addEventListener('click', () => this.setLogPanel(false));
    this.byId('logClear').addEventListener('click', () => {
      if (this.logView === 'journal') void clearJournal().then(() => { this.renderLog(); this.updateLogCount(); });
      else { clearLog(); this.renderLog(); this.updateLogCount(); }
    });
    this.wireLogResize();

    // Paint whatever buffered before mount, then stream one node per line.
    this.renderLog();
    onLog((l) => {
      if (!this.logOpen || this.logView !== 'log') {
        this.updateLogCount();
        return;
      }
      this.appendLog(logRow(l));
      this.updateLogCount();
    });
    onJournal((e) => {
      if (!this.logOpen || this.logView !== 'journal') {
        this.updateLogCount();
        return;
      }
      this.appendLog(journalRow(e));
      this.updateLogCount();
    });
    this.byId('logTabLog').addEventListener('click', () => this.setLogView('log'));
    this.byId('logTabJournal').addEventListener('click', () => this.setLogView('journal'));
    this.byId('logExport').addEventListener('click', () => void this.downloadJournal());
    void loadJournal().then(() => { if (this.logView === 'journal') this.renderLog(); this.updateLogCount(); });
    this.setLogView((localStorage.getItem('kg_log_view') as 'log' | 'journal') || 'log');
    this.applyLogPanel();
  }

  private wireLogResize(): void {
    this.byId('logDrag').addEventListener('pointerdown', (ev) => {
      ev.preventDefault();
      const startY = (ev as PointerEvent).clientY;
      const startH = this.logHeight;
      const move = (e: PointerEvent) => {
        this.logHeight = clampHeight(startH + (startY - e.clientY));
        this.byId('logPanel').style.height = `${this.logHeight}px`;
      };
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        this.persistLogPanel();
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    });
  }

  private setLogPanel(open: boolean): void {
    this.logOpen = open;
    if (open) this.renderLog();
    this.applyLogPanel();
    this.persistLogPanel();
    this.applySidebar(); // repaint the rail's active markers
  }

  private applyLogPanel(): void {
    const el = this.byId('logPanel');
    el.style.display = this.logOpen ? '' : 'none';
    el.style.height = `${this.logHeight}px`;
    this.byId('logCount').textContent = String(logLines().length);
  }

  private persistLogPanel(): void {
    localStorage.setItem(LOGPANEL_KEY, JSON.stringify({ open: this.logOpen, h: this.logHeight }));
  }

  private logView: 'log' | 'journal' = 'log';

  private setLogView(view: 'log' | 'journal'): void {
    this.logView = view;
    localStorage.setItem('kg_log_view', view);
    this.byId('logTabLog').classList.toggle('on', view === 'log');
    this.byId('logTabJournal').classList.toggle('on', view === 'journal');
    this.byId('logExport').style.display = view === 'journal' ? '' : 'none';
    this.renderLog();
    this.updateLogCount();
  }

  private updateLogCount(): void {
    this.byId('logCount').textContent = String(this.logView === 'journal' ? journalEntries().length : logLines().length);
  }

  private async downloadJournal(): Promise<void> {
    const text = await exportJournal();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([text], { type: 'application/x-ndjson' }));
    a.download = `kladder-journal-${new Date().toISOString().replace(/[:.]/g, '-')}.ndjson`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  }

  private renderLog(): void {
    const body = this.byId('logBody');
    body.replaceChildren();
    if (this.logView === 'journal') for (const e of journalEntries()) body.appendChild(journalRow(e));
    else for (const l of logLines()) body.appendChild(logRow(l));
    body.scrollTop = body.scrollHeight;
  }

  /** Only auto-scrolls when already at the bottom, so reading older lines is
   *  not yanked away by the next request. */
  private appendLog(row: HTMLElement): void {
    const body = this.byId('logBody');
    const pinned = body.scrollHeight - body.scrollTop - body.clientHeight < 24;
    body.appendChild(row);
    while (body.childElementCount > 400) body.firstElementChild!.remove();
    if (pinned) body.scrollTop = body.scrollHeight;
  }

  private byId<T extends HTMLElement = HTMLElement>(id: string): T {
    return document.getElementById(id) as T;
  }

  private engineFor(venue: VenueId): VenueEngine {
    return venue === 'polymarket' ? this.pmEngine : venue === 'arb' ? this.arbEngine : this.engine;
  }

  private hostFor(venue: VenueId): PanelHost {
    const engine = this.engineFor(venue);
    return {
      engine,
      accounts: () => this.accountsList,
      panels: () => this.panels.filter((p) => p.venue === venue),
      isStale: () => (venue === 'polymarket' ? !this.pmEngine.live : this.engine.stale),
      // Simulated arb quotes show on a leg's ladder only for the account they were placed under.
      phantoms: venue === 'arb' ? undefined : (t, acct) => this.arbEngine.phantomsFor(venue, t, acct),
      isStarred: (t) => getStarred().includes(starKey(venue, t)),
      toggleStar: (t) => {
        toggleStar(starKey(venue, t));
        this.renderStarred();
        this.panels.forEach((p) => {
          if (p.venue === venue && p.ticker === t) p.syncStar();
        });
      },
      onTickerChange: () => {
        this.updateInfo();
        this.saveLayout();
      },
    };
  }

  /** Adds a ladder on a venue. Returns null when the grid has no free slot for one. */
  private addPanel(saved?: SavedPanel, venue: VenueId = saved?.venue === 'polymarket' ? 'polymarket' : 'kalshi'): Panel | null {
    const id = saved?.id ?? newPanelId();
    const p = new Panel(id, this.hostFor(venue), saved?.mode);
    // Registered before grid.add: onChange persists by intersecting grid rects with this list.
    this.panels.push(p);
    if (!this.grid.add(id, p.el, saved?.rect)) {
      this.panels.pop();
      return null;
    }
    this.grid.setVenue(id, venue === 'polymarket' ? 'P' : 'K', p.venueLabel);
    if (saved?.ticker) p.select(saved.ticker);
    this.grid.setTitle(id, p.ticker ?? 'empty');
    this.renderStarred();
    this.updateInfo();
    return p;
  }

  /** An arb ladder: two legs, no venue of its own. */
  private addArbPanel(saved?: SavedPanel): ArbPanel | null {
    const id = saved?.id ?? newPanelId();
    const a = new ArbPanel(id, {
      arb: this.arbEngine,
      engineFor: (v) => this.engineFor(v),
      venues: () => KNOWN_VENUES.filter((v) => v.id === 'kalshi' || this.venues.some((x) => x.id === v.id && x.hasKey))
        .map((v) => ({ id: v.id as RealVenueId, label: v.label })),
      accounts: () => this.accountsList,
      onChange: () => {
        this.grid.setTitle(id, a.title());
        this.saveLayout();
      },
    }, saved?.legs, saved?.cross ?? true);
    this.arbs.push(a);
    if (!this.grid.add(id, a.el, saved?.rect)) {
      this.arbs.pop();
      a.destroy();
      return null;
    }
    this.grid.setVenue(id, 'A', 'Arb: quoter − hedger');
    this.grid.setTitle(id, a.title());
    this.updateInfo();
    return a;
  }

  /** A panel is only a view — brackets live in the engine, so removing one cannot orphan a hedge. */
  private confirmRemove(id: string): boolean {
    const arb = this.arbs.find((x) => x.id === id);
    if (arb) {
      arb.destroy();
      this.arbs = this.arbs.filter((x) => x.id !== id);
      this.updateInfo();
      return true;
    }
    const p = this.panels.find((x) => x.id === id);
    if (!p) return true;
    if (p.ticker) {
      const armed = p.venue === 'kalshi' ? [...this.engine.brackets.values()].filter((b) => b.ticker === p.ticker).length : 0;
      if (armed && !confirm(
        `${armed} bracket${armed === 1 ? '' : 's'} armed on ${p.ticker}.\n\n`
        + 'They keep running in the background and still hedge — you just lose sight of them.\n\n'
        + 'Remove this ladder anyway?')) {
        return false;
      }
      p.clearTicker(); // releases the WS subscription (refcounted)
    }
    // Drop the account binding with the panel; its id is never reused.
    localStorage.removeItem('kg_acct_' + id);
    this.panels = this.panels.filter((x) => x.id !== id);
    this.updateInfo();
    return true;
  }

  private updateInfo(): void {
    const n = this.panels.length + this.arbs.length;
    this.byId('gridEmpty').style.display = n ? 'none' : '';
    for (const p of this.panels) this.grid.setTitle(p.id, p.ticker ?? 'empty');
    for (const a of this.arbs) this.grid.setTitle(a.id, a.title());
  }

  private saveLayout(): void {
    const byId = new Map(this.panels.map((p) => [p.id, p]));
    const arbById = new Map(this.arbs.map((a) => [a.id, a]));
    const saved: SavedPanel[] = this.grid.rects()
      .filter((r) => byId.has(r.id) || arbById.has(r.id))
      .map((r) => {
        const a = arbById.get(r.id);
        if (a) return { id: r.id, rect: r.rect, ticker: null, venue: 'arb' as const, legs: a.legs, cross: a.crossing };
        const p = byId.get(r.id)!;
        return { id: r.id, rect: r.rect, ticker: p.ticker, mode: p.mode, venue: p.venue };
      });
    localStorage.setItem(LAYOUT_KEY, JSON.stringify(saved));
    localStorage.setItem(LAYOUT_CELL_KEY, String(cellPx()));
  }

  /** Settings slider for the grid's cell size; ladders keep their on-screen size as it changes. */
  private wireGridSize(): void {
    const input = this.byId<HTMLInputElement>('setGridCell');
    const val = this.byId('setGridCellVal');
    input.value = String(cellPx());
    val.textContent = `${cellPx()}px`;
    input.addEventListener('input', () => {
      this.grid.setCell(Number(input.value));
      val.textContent = `${cellPx()}px`;
      localStorage.setItem(GRID_CELL_KEY, String(cellPx()));
    });
  }

  /** Tickers are restored too, via `select()`, so ladders come back populated. */
  private restoreLayout(): void {
    let saved: SavedPanel[] = [];
    try {
      const raw = JSON.parse(localStorage.getItem(LAYOUT_KEY) || '[]');
      if (Array.isArray(raw)) saved = raw.filter(isSavedPanel);
    } catch {
      saved = [];
    }
    // A layout saved at another cell size is re-expressed at this one, so ladders keep their on-screen size.
    const from = Number(localStorage.getItem(LAYOUT_CELL_KEY)) || LEGACY_CELL;
    if (from !== cellPx()) saved = saved.map((s) => ({ ...s, rect: scaleRect(s.rect, from, cellPx()) }));
    for (const s of saved) {
      if (s.venue === 'arb') this.addArbPanel(s);
      else this.addPanel(s);
    }
    this.renderStarred();
    this.updateInfo();
  }

  private wireEngine(): void {
    // Each venue engine drives only its own ladders, routed explicitly by venue.
    for (const engine of [this.engine, this.pmEngine] as VenueEngine[]) {
      const mine = (ticker: string) => this.panels.filter((p) => p.venue === engine.venue && p.ticker === ticker);
      engine.on('snapshot', (msg) => { for (const p of mine(msg.ticker)) p.onSnapshot(msg); });
      engine.on('trade', ({ ticker, trade }) => { for (const p of mine(ticker)) p.onTrade(trade); });
      if (engine.venue !== 'kalshi') engine.on('fill', (ev) => { for (const p of mine(ev.ticker)) p.onFillEvent(ev); });
    }
    this.engine.on('pnl', (msg) => {
      this.lastPnl = msg;
      this.renderPnl(msg);
    });
    this.engine.on('fill', (ev) => {
      for (const p of this.panels) if (p.venue === 'kalshi') p.onFillEvent(ev);
    });
    this.engine.on('accounts', ({ leaves }) => {
      this.accountsList = leaves;
      this.panels.forEach((p) => p.refreshAcct());
      // Polymarket's view is split by these same accounts, so it redraws when they change, not only on its own poll.
      if (this.pmEngine.feed.account.at) this.renderPolymarket(this.pmEngine.feed.account);
    });
    this.engine.on('status', ({ ws, detail, stale }) => this.renderStatus(ws, detail, stale));
    this.engine.on('log', ({ level, msg }) => {
      if (level === 'error') toast(msg, 'error');
    });
  }

  private renderStatus(ws: string, detail: string | undefined, stale: boolean): void {
    // Stale is red, not amber: it's the same practical state as disconnected.
    const good = ws === 'open' && !stale;
    const label = good ? 'Connected' : ws === 'open' ? 'Stale' : 'Disconnected';

    this.byId('wsChip').className = 'tb-remote ' + (good ? 'ok' : 'bad');
    this.byId('wsIcon').innerHTML = REMOTE_SVG(!good);
    this.byId('wsInfo').textContent = label;

    this.byId('popState').textContent = label;
    this.byId('popHost').textContent = this.wsHost || '—';
    this.byId('popSubs').textContent = `${this.panels.filter((p) => p.ticker).length} ladder(s)`;
    // Only a fault is worth a line; while connected it would just repeat the heading.
    this.byId('popDetailRow').style.display = good ? 'none' : '';
    this.byId('popDetail').textContent = detail || ws;
    this.byId('popNote').textContent = good ? ''
      : this.engine.isStarted
        ? 'Prices are not updating. Order placement is blocked until the feed recovers.'
        : 'Not connected yet. Load the extension, then approve this page in it.';

    // Dim panels while stale: clicking a ladder that stopped updating is how you cross by accident.
    for (const p of this.panels) {
      if (p.venue !== 'kalshi') continue;
      p.el.style.opacity = stale ? '0.6' : '';
      p.el.style.filter = stale ? 'grayscale(0.8)' : '';
    }
    // Banner only once there's a ladder actually watching a feed that dropped out.
    const watching = this.engine.isStarted && this.panels.some((p) => p.ticker);
    setBanner('kg-ws-banner', stale && watching ? 'DISCONNECTED — data stale, orders blocked' : null);
  }

  private shardPop: HTMLDivElement | null = null;
  private shardPopOff: (() => void) | null = null;

  private async toggleShardPop(): Promise<void> {
    if (this.shardPop) {
      this.closeShardPop();
      return;
    }
    const pop = document.createElement('div');
    pop.className = 'kg-menu shard-pop';
    pop.innerHTML = '<div class="shard-head"><span>Exchange shards</span><span class="shard-note">loading…</span></div>';
    document.body.appendChild(pop);
    this.shardPop = pop;
    const place = () => {
      const r = this.byId('shardsBtn').getBoundingClientRect();
      pop.style.left = `${Math.max(6, r.right - pop.offsetWidth)}px`;
      pop.style.top = `${Math.max(6, r.top - pop.offsetHeight - 6)}px`;
    };
    place();
    const onDown = (ev: MouseEvent) => {
      if (!pop.contains(ev.target as Node) && ev.target !== this.byId('shardsBtn')) this.closeShardPop();
    };
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') this.closeShardPop(); };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    this.shardPopOff = () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
    await this.renderShardPop();
    place();
  }

  private closeShardPop(): void {
    this.shardPop?.remove();
    this.shardPop = null;
    this.shardPopOff?.();
    this.shardPopOff = null;
  }

  private async renderShardPop(note = ''): Promise<void> {
    const pop = this.shardPop;
    if (!pop) return;
    let shards: ShardBalance[] = [];
    let alloc: ShardAllocation[] = [];
    let error = '';
    try {
      [{ shards }, alloc] = await Promise.all([this.engine.shardBalances(), this.engine.targetAllocation().catch(() => [] as ShardAllocation[])]);
    } catch (e) {
      error = (e as Error).message;
    }
    if (!this.shardPop) return;
    const known = new Set(shards.map((s) => s.exchangeIndex));
    for (const i of Object.keys(SHARD_NAMES).map(Number)) if (!known.has(i)) shards.push({ exchangeIndex: i, balanceCash: 0 });
    shards.sort((a, b) => a.exchangeIndex - b.exchangeIndex);
    const name = (i: number) => SHARD_NAMES[i] ?? `Shard ${i}`;
    const total = shards.reduce((a, s) => a + s.balanceCash, 0);
    // Sliders start at the allocation Kalshi has; with none set, at today's split.
    const pct = new Map<number, number>();
    if (alloc.length) for (const a of alloc) pct.set(a.exchangeIndex, a.percent);
    else for (const s of shards) pct.set(s.exchangeIndex, total > 0 ? Math.round((s.balanceCash / total) * 100) : 0);
    const applied = new Map(pct);
    pop.innerHTML = `
      <div class="shard-head"><span>Exchange shards</span><span class="shard-note">${escHtml(note || (error ? error : `$${formatCash(total)} total`))}</span></div>
      <table class="shard-table">
        ${shards.map((s) => `
          <tr data-shard="${s.exchangeIndex}">
            <td class="shard-idx">${s.exchangeIndex}</td>
            <td class="shard-name">${escHtml(name(s.exchangeIndex))}</td>
            <td class="shard-bal">$${formatCash(s.balanceCash)}</td>
            <td class="shard-slide"><input type="range" min="0" max="100" step="1" value="${pct.get(s.exchangeIndex) ?? 0}" data-shard="${s.exchangeIndex}"/></td>
            <td class="shard-pct"><span class="js-pct" data-shard="${s.exchangeIndex}">${pct.get(s.exchangeIndex) ?? 0}</span>%</td>
          </tr>`).join('')}
      </table>
      <div class="shard-row">
        <span class="shard-status js-status"></span>
        <span class="tb-spacer"></span>
        <button class="lp-btn js-auto-off" ${alloc.length ? '' : 'disabled'} title="Clear the allocation; Kalshi stops moving money between shards">Turn off</button>
        <button class="sb-foot-btn shard-apply js-move" title="Transfer between shards now, directly, so the balances match this split. Does not touch the rebalancer setting.">Move now</button>
        <button class="sb-foot-btn shard-apply js-apply" title="Hand this split to Kalshi's rebalancer as a standing target">Apply</button>
      </div>
      <div class="set-note"><b>Move now</b> transfers directly (a few seconds). <b>Apply</b> sets Kalshi's own rebalancer: every 10 seconds it is meant to move money toward these percentages, counting collateral on resting orders as already placed. ${alloc.length ? 'Currently <b>on</b>.' : 'Currently <b>off</b> — balances stay where they are.'}</div>`;
    const sliders = [...pop.querySelectorAll<HTMLInputElement>('input[type=range]')];
    const status = pop.querySelector<HTMLElement>('.js-status')!;
    const apply = pop.querySelector<HTMLButtonElement>('.js-apply')!;
    const move = pop.querySelector<HTMLButtonElement>('.js-move')!;
    // Fills each under-target shard from over-target shards, largest surplus first.
    const MIN_MOVE_CASH = 10_000;
    const planMoves = (): { from: number; to: number; cash: number }[] => {
      const have = new Map(shards.map((sh) => [sh.exchangeIndex, sh.balanceCash]));
      const want = sliders.map((sl) => ({ idx: Number(sl.dataset.shard), cash: Math.round((total * Number(sl.value)) / 100) }));
      const surplus = want.map((w) => ({ idx: w.idx, cash: (have.get(w.idx) ?? 0) - w.cash })).filter((x) => x.cash >= MIN_MOVE_CASH).sort((a, b) => b.cash - a.cash);
      const deficit = want.map((w) => ({ idx: w.idx, cash: w.cash - (have.get(w.idx) ?? 0) })).filter((x) => x.cash >= MIN_MOVE_CASH).sort((a, b) => b.cash - a.cash);
      const moves: { from: number; to: number; cash: number }[] = [];
      for (const d of deficit) {
        let need = d.cash;
        for (const sp of surplus) {
          if (need < MIN_MOVE_CASH) break;
          const amt = Math.min(need, sp.cash);
          if (amt < MIN_MOVE_CASH) continue;
          moves.push({ from: sp.idx, to: d.idx, cash: amt });
          sp.cash -= amt;
          need -= amt;
        }
      }
      return moves;
    };
    const paint = () => {
      let sum = 0;
      for (const sl of sliders) {
        const v = Number(sl.value);
        sum += v;
        pop.querySelector<HTMLElement>(`.js-pct[data-shard="${sl.dataset.shard}"]`)!.textContent = String(v);
      }
      const changed = sliders.some((sl) => Number(sl.value) !== (applied.get(Number(sl.dataset.shard)) ?? 0));
      status.textContent = sum === 100 ? (changed ? `$${formatCash(total)} split as shown` : alloc.length ? 'as set on Kalshi' : '') : `adds up to ${sum}%, needs 100`;
      status.classList.toggle('bad', sum !== 100);
      apply.disabled = sum !== 100 || !changed;
      const moves = sum === 100 ? planMoves() : [];
      move.disabled = moves.length === 0;
      move.title = moves.length
        ? `Transfer now: ${moves.map((m) => `$${formatCash(m.cash)} ${name(m.from)} → ${name(m.to)}`).join(', ')}`
        : 'Balances already match this split';
      // The check means Kalshi reports this exact split, read back from it.
      const verified = !changed && alloc.length > 0 && sum === 100;
      apply.textContent = verified ? 'Applied' : 'Apply';
      apply.classList.toggle('applied', verified);
    };
    // Moving one slider redistributes the remainder proportionally; rounding slack lands on the largest.
    for (const sl of sliders) {
      sl.addEventListener('input', () => {
        const v = Number(sl.value);
        const others = sliders.filter((o) => o !== sl);
        const rest = 100 - v;
        const had = others.map((o) => Number(o.value));
        const hadSum = had.reduce((a, b) => a + b, 0);
        let assigned = 0;
        others.forEach((o, k) => {
          const share = hadSum > 0 ? Math.floor((had[k] / hadSum) * rest) : Math.floor(rest / others.length);
          o.value = String(share);
          assigned += share;
        });
        const slack = rest - assigned;
        if (slack > 0) {
          const biggest = others.reduce((a, b) => (Number(b.value) >= Number(a.value) ? b : a), others[0]);
          if (biggest) biggest.value = String(Number(biggest.value) + slack);
        }
        paint();
      });
    }
    paint();
    apply.addEventListener('click', () => {
      // Every shard goes in the list, 0% included — an allocation omitting a shard leaves stray cash on it.
      const allocations = sliders.map((sl) => ({ exchangeIndex: Number(sl.dataset.shard), percent: Number(sl.value) }));
      apply.disabled = true;
      apply.textContent = 'Applying…';
      void this.engine.setTargetAllocation(allocations)
        .then(async () => {
          // Read it back: the check is only shown for what Kalshi confirms.
          const now = await this.engine.targetAllocation();
          // Kalshi may echo the list without its 0% rows; compare the funded shards only.
          const set = allocations.filter((a) => a.percent > 0);
          const got = now.filter((n) => n.percent > 0);
          const same = set.length === got.length
            && set.every((a) => got.some((n) => n.exchangeIndex === a.exchangeIndex && n.percent === a.percent));
          if (!same) {
            toast(`Kalshi accepted the request but reports a different split: ${now.length ? now.map((n) => `${name(n.exchangeIndex)} ${n.percent}%`).join(', ') : 'none'}`, 'error');
            await this.renderShardPop('not applied — Kalshi reports a different split');
            return;
          }
          toast(`Allocation applied: ${set.map((a) => `${name(a.exchangeIndex)} ${a.percent}%`).join(', ')}`, 'ok');
          await this.settleShardPop();
        })
        .catch((e) => { toast('Kalshi refused the allocation: ' + (e as Error).message, 'error'); apply.textContent = 'Apply'; apply.disabled = false; });
    });
    move.addEventListener('click', async () => {
      const moves = planMoves();
      if (!moves.length) return;
      move.disabled = true;
      apply.disabled = true;
      move.textContent = 'Moving…';
      let done = 0;
      try {
        // One at a time so a mid-way refusal leaves a readable trail, not a race.
        for (const m of moves) {
          await this.engine.transferShards(m.from, m.to, m.cash);
          done++;
        }
        toast(`${done} transfer${done === 1 ? '' : 's'} sent: ${moves.map((m) => `$${formatCash(m.cash)} ${name(m.from)} → ${name(m.to)}`).join(', ')}`, 'ok');
      } catch (e) {
        toast(`Transfer refused after ${done} of ${moves.length}: ${(e as Error).message}`, 'error');
      }
      await this.settleShardPop();
    });
    pop.querySelector('.js-auto-off')!.addEventListener('click', () => {
      void this.engine.setTargetAllocation([]).then(() => { toast('Auto-rebalance turned off', 'ok'); void this.renderShardPop(); })
        .catch((e) => toast('Could not change auto-rebalance: ' + (e as Error).message, 'error'));
    });
  }

  /** Kalshi sweeps every 10 seconds: re-read the balances until the split shows. */
  private async settleShardPop(): Promise<void> {
    for (let i = 0; i < 8 && this.shardPop; i++) {
      await new Promise((r) => setTimeout(r, 1_500));
      await this.renderShardPop(i < 7 ? 'waiting for Kalshi to rebalance…' : '');
    }
  }

  private async rebuild(): Promise<void> {
    const btn = this.byId<HTMLButtonElement>('rebuildBtn');
    btn.disabled = true;
    btn.textContent = 'Rebuilding…';
    try {
      const r = await this.engine.rebuildLedgerFromKalshi();
      toast(`Ledger rebuilt — ${r.fetched} fills from Kalshi, ${r.added} new`, 'ok');
    } catch (e) {
      toast('Rebuild failed: ' + (e as Error).message, 'error');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Rebuild ledger';
    }
  }

  /** Warns when a second tab is open: two tabs would both post bracket hedges off the same ledger. */
  private guardMultipleTabs(): void {
    const chan = new BroadcastChannel('kladder');
    chan.postMessage({ type: 'hello' });
    chan.addEventListener('message', (ev) => {
      if (ev.data?.type === 'hello') {
        chan.postMessage({ type: 'already-open' });
      } else if (ev.data?.type === 'already-open') {
        setBanner(
          'kg-multitab',
          'Another Kladder tab is open. Close it — two tabs will double-hedge brackets.',
          'warn',
        );
      }
    });
  }

  private wireUnload(): void {
    // pagehide is the reliable hook here; beforeunload doesn't fire on mobile/bfcache paths.
    window.addEventListener('pagehide', () => {
      this.arbEngine.cancelAllQuotes(); // best effort; Kalshi quotes are also parked with the deadman
      this.engine.fireDeadman();
    });
    window.addEventListener('beforeunload', (ev) => {
      if (!this.engine.hasArmedBrackets()) return;
      ev.preventDefault();
      ev.returnValue = '';
    });
  }

  private renderPnl(msg: PnlSnapshot): void {
    this.byId('kBalance').textContent = '$' + formatCash(msg.balanceCash);
    const kPnl = this.byId('kTotalPnl');
    kPnl.innerHTML = unitHtml(money(msg.totalPnlCash));
    kPnl.style.color = pnlColor(msg.totalPnlCash);
    this.renderConsolidated();

    this.renderAccounts(this.byId('pnlPositions'), msg);
    void this.renderFillsByAccount();
    this.renderOrdersOut(this.byId('pnlOrdersOut'), msg);
    renderSettledRows(this.byId('pnlRecent'), msg.settled_recent, 'No settlements in last 24h');
    renderSettledRows(this.byId('pnlSettled'), msg.settled, 'No older settled trades');
  }

  /** Venue views come and go with their keys; Kalshi is the engine's venue and is always there. */
  private renderVenues(s: ConnectionStatus): void {
    this.venues = s.venues;
    const has = (id: string) => id === 'kalshi' || s.venues.some((v) => v.id === id && v.hasKey);

    for (const b of document.querySelectorAll<HTMLElement>('.ab-btn[data-venue]')) {
      b.style.display = has(b.dataset.venue!) ? '' : 'none';
    }
    const active = VIEWS.find((v) => v.id === this.activeView);
    if (active?.venue && !has(active.venue)) this.activeView = VIEWS[0].id;

    for (const v of s.venues) {
      // The venue's light is a full stop on its rail letter; nowhere else in the P&L views.
      const btn = document.querySelector<HTMLElement>(`.ab-btn[data-venue="${v.id}"]`);
      btn?.querySelector('.ab-dot')?.setAttribute('class', 'ab-dot ' + venueDotClass(v.state));
      this.titleVenueIcon(v);
    }

    for (const a of this.arbs) a.refreshVenues();
    /* The "+" menu lists only venues with a key, so it is one row with Kalshi
     * alone and grows a row per exchange plugged in. */
    for (const b of document.querySelectorAll<HTMLElement>('.fab-item[data-venue]')) {
      b.style.display = b.dataset.venue === 'arb' || has(b.dataset.venue!) ? '' : 'none';
    }
    const pm = s.venues.find((v) => v.id === 'polymarket');
    // The feed follows the key: it starts on the first poll that shows one and
    // stops on the first poll that does not.
    if (pm?.hasKey && pm.wsUrl) {
      if (!this.pmEngine.feed.running) this.pmEngine.feed.start(pm.wsUrl, pm.privateWsUrl ?? undefined);
    } else if (this.pmEngine.feed.running) {
      this.pmEngine.feed.stop();
    }

    this.byId('profVenues').innerHTML = s.venues.map((v) =>
      `<div class="pnl-row"><span class="pnl-lbl">${escHtml(v.label)}</span>`
      + `<span class="pnl-val mono" style="font-size:11px">${v.hasKey ? escHtml(`${v.keyIdPrefix}… · ${venueStateLabel(v.state)}`) : 'no key'}</span></div>`).join('');

    this.applySidebar(); // the rail follows the venues
    this.renderConsolidated();
  }

  /** The venue icon's tooltip carries what the Status row used to: state, its detail, and Polymarket's feed. */
  private titleVenueIcon(v: VenueStatus): void {
    const btn = document.querySelector<HTMLElement>(`.ab-btn[data-venue="${v.id}"]`);
    if (!btn) return;
    btn.title = [`${v.label}: ${venueStateLabel(v.state)}`, v.detail, v.id === 'polymarket' ? this.pmFeedText : '']
      .filter(Boolean).join('\n');
  }

  /** One row per known venue with its live numbers, each a shortcut to that venue's view. */
  private renderConsolidated(): void {
    const k = this.lastPnl;
    const pmOn = this.venues.some((v) => v.id === 'polymarket' && v.hasKey);
    const pmAcct = this.pmEngine.feed.account;
    const pmBal = pmAcct.balance?.balanceCash ?? 0;
    const pmPnl = this.lastPmPnl?.totalPnlCash ?? pmPnlOf(pmAcct);
    const bal = (k?.balanceCash ?? 0) + (pmOn ? pmBal : 0);
    const pnl = (k?.totalPnlCash ?? 0) + (pmOn ? pmPnl : 0);
    this.byId('accBalance').textContent = k ? '$' + formatCash(bal) : '—';
    const pnlEl = this.byId('accTotalPnl');
    pnlEl.innerHTML = unitHtml(k ? money(pnl) : '—');
    pnlEl.style.color = pnlColor(pnl);

    const el = this.byId('consolidatedByVenue');
    el.replaceChildren();
    const venues = this.venues.length ? this.venues : [{ id: 'kalshi', label: 'Kalshi', hasKey: true, state: 'checking' as const }];
    for (const v of venues) {
      const row = document.createElement('div');
      row.className = 'consol-row' + (v.hasKey ? ' live' : '');
      const figures = v.id === 'kalshi'
        ? (k ? `$${formatCash(k.balanceCash)} · ${money(k.totalPnlCash)}` : '—')
        : v.hasKey ? (pmAcct.at ? `$${formatCash(pmBal)} · ${money(pmPnl)}` : pmAcct.error ? '—' : 'loading…') : 'not set up';
      row.innerHTML = `<span class="consol-name">${escHtml(v.label)}</span>`
        + `<span class="mono">${figures.split(' · ').map(unitHtml).join(' · ')}</span>`;
      if (v.hasKey && VIEWS.some((x) => x.id === v.id)) {
        row.title = `Open the ${v.label} view`;
        row.addEventListener('click', () => this.selectView(v.id));
      }
      el.appendChild(row);
    }
  }

  /** The Polymarket view: the same sections as Kalshi's, built from the feed's last account snapshot. */
  private renderPolymarket(a: PmAccount): void {
    const bal = a.balance;
    // An unfunded account answers with no balance rows at all, not a zero row.
    this.byId('pmBalance').innerHTML = unitHtml(bal ? '$' + formatCash(bal.balanceCash) : a.at ? '$0.00' : a.error ? '—' : 'loading…');
    this.byId('pmBuyingPower').textContent = bal ? '$' + formatCash(bal.buyingPowerCash) : a.at ? '$0.00' : '—';
    const msg = this.pmEngine.buildPnlSnapshot(this.accountParts());
    this.lastPmPnl = a.at ? msg : null;
    const pnlEl = this.byId('pmTotalPnl');
    pnlEl.innerHTML = unitHtml(a.at ? money(msg.totalPnlCash) : '—');
    pnlEl.style.color = pnlColor(msg.totalPnlCash);

    const accEl = this.byId('pmAccounts');
    if (!a.at) accEl.innerHTML = a.error ? `<div class="pnl-empty">${escHtml(a.error)}</div>` : skeletonHtml(3);
    else this.renderAccounts(accEl, msg, 'polymarket');
    this.renderPmFills();
    this.renderOrdersOut(this.byId('pmOrdersOut'), msg, 'polymarket');
    renderSettledRows(this.byId('pmRecent'), msg.settled_recent, 'No settlements in last 24h');
    renderSettledRows(this.byId('pmSettled'), msg.settled, 'No older settled trades');
    this.renderConsolidated();
  }

  private renderPmFills(): void {
    const accts = this.accountParts();
    const names = new Map(accts.map((x) => [x.id, x.name]));
    this.renderFillGroups(this.byId('pmFillsByAcct'), this.pmEngine.fillsByAccount(accts), names, 'polymarket', false);
  }

  /** App accounts with their name paths; shared by every venue. */
  private accountParts(): { id: string; name: string; parts: string[] }[] {
    return this.engine.accounts.leaves().map((x) => ({ id: x.id, name: x.name, parts: x.parts }));
  }

  /** What a list shows for a market: Kalshi tickers read as-is, Polymarket slugs by their title. */
  private labelOf(venue: 'kalshi' | 'polymarket', ticker: string): string {
    return venue === 'polymarket' ? this.pmEngine.titleOf(ticker) : ticker;
  }

  /** Kalshi's fills by account, editable. */
  private async renderFillsByAccount(): Promise<void> {
    const el = this.byId('pnlFillsByAcct');
    let groups: Map<string, FillRow[]>;
    try {
      groups = await this.engine.fillsByAccount();
    } catch (e) {
      el.innerHTML = `<div class="pnl-empty">${escHtml((e as Error).message)}</div>`;
      return;
    }
    const names = new Map(this.engine.accounts.listLeaves().map((a) => [a.id, a.name]));
    this.renderFillGroups(el, groups, names, 'kalshi', this.fillsEdit);
  }

  /** One collapsible group per account, listing its fills, capped with a "show more". Editing is Kalshi's only. */
  private renderFillGroups(el: HTMLElement, groups: Map<string, FillRow[]>, names: Map<string, string>, venue: 'kalshi' | 'polymarket', edit: boolean): void {
    const st = this.listState[venue];
    const rerender = () => (venue === 'kalshi' ? void this.renderFillsByAccount() : this.renderPmFills());
    el.replaceChildren();
    let any = false;

    for (const [aid, fills] of [...groups.entries()].sort(
      (a, b) => (names.get(a[0]) ?? a[0]).localeCompare(names.get(b[0]) ?? b[0]),
    )) {
      if (!names.has(aid) && !fills.length) continue;
      any = any || fills.length > 0;
      const collapsed = st.fills.get(aid) ?? true;
      const shown = st.shown.get(aid) ?? FILLS_PAGE;

      const group = document.createElement('div');
      group.className = 'fill-group';
      group.dataset.acct = aid;

      const hd = document.createElement('div');
      hd.className = 'fill-group-hd';
      hd.innerHTML = `<span><span class="caret${collapsed ? '' : ' open'}"></span> `
        + `${escHtml(names.get(aid) ?? aid)}</span>`
        + `<span class="mono" style="opacity:.7">${fills.length}</span>`;
      hd.addEventListener('click', () => {
        st.fills.set(aid, !collapsed);
        rerender();
      });
      // Never on mobile — it's the catch-all every deleted account's fills are rebooked to.
      if (edit && aid !== MOBILE_ID) {
        const del = document.createElement('span');
        del.className = 'fill-del';
        del.textContent = '✕';
        del.title = 'Delete this account and reboot its fills to mobile';
        del.addEventListener('click', (ev) => {
          ev.stopPropagation();
          const label = names.get(aid) ?? aid;
          if (!confirm(`Delete "${label}"?\n\n`
            + `Its ${fills.length} fill(s) move to mobile — nothing is deleted from your history, `
            + 'and the totals still tie back to Kalshi. This only affects this browser.')) return;
          this.engine.deleteAccount(aid)
            .then(({ moved }) => { toast(`Deleted ${label} — ${moved} fill(s) moved to mobile`, 'ok'); })
            .catch((e) => toast(`Delete failed — ${(e as Error).message}`));
        });
        hd.appendChild(del);
      }
      group.appendChild(hd);

      if (!collapsed) {
        if (!fills.length) {
          const empty = document.createElement('div');
          empty.className = 'pnl-empty';
          empty.style.padding = '3px 8px';
          empty.textContent = 'no fills';
          group.appendChild(empty);
        }
        for (const f of fills.slice(0, shown)) {
          const row = document.createElement('div');
          row.className = 'fill-row';
          row.dataset.fill = f.fill_id;
          if (edit) row.draggable = true;
          const moved = venue === 'kalshi' && this.engine.isFillMoved(f.fill_id);
          const t = new Date(f.ts * 1000);
          const clock = `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
          const day = `${t.getMonth() + 1}/${t.getDate()}`;
          row.innerHTML =
            `<span class="fill-tk mono" title="${escHtml(f.ticker)}" data-copy="${escHtml(f.ticker)}">${escHtml(this.labelOf(venue, f.ticker))}</span>`
            + `<span class="fill-sd ${f.side}">${f.side.toUpperCase()}</span>`
            + `<span class="fill-q mono">${formatMq(f.mq)}</span>`
            + `<span class="fill-px mono">${unitHtml(`${(f.yes_price_mc / 10).toFixed(1)}¢`)}</span>`
            + `<span class="fill-ts mono">${day} ${clock}</span>`
            // A moved fill is marked: a rebuild cannot recreate a hand-set assignment.
            + (moved ? '<span class="fill-moved" title="Moved by hand — reset to re-derive">•</span>' : '');
          if (edit) {
            row.addEventListener('dragstart', (ev) => {
              ev.dataTransfer?.setData('text/plain', f.fill_id);
              row.classList.add('dragging');
            });
            row.addEventListener('dragend', () => row.classList.remove('dragging'));
            if (moved) {
              row.querySelector('.fill-moved')?.addEventListener('click', (ev) => {
                ev.stopPropagation();
                void this.engine.resetFillAccount(f.fill_id).then(() => this.renderFillsByAccount());
              });
            }
          }
          group.appendChild(row);
        }
        if (fills.length > shown) {
          const more = document.createElement('div');
          more.className = 'fill-more';
          more.textContent = `show ${Math.min(FILLS_PAGE, fills.length - shown)} more of ${fills.length - shown}`;
          more.addEventListener('click', () => {
            st.shown.set(aid, shown + FILLS_PAGE);
            rerender();
          });
          group.appendChild(more);
        }
      }

      if (edit) {
        group.addEventListener('dragover', (ev) => {
          ev.preventDefault();
          group.classList.add('drop-target');
        });
        group.addEventListener('dragleave', () => group.classList.remove('drop-target'));
        group.addEventListener('drop', (ev) => {
          ev.preventDefault();
          group.classList.remove('drop-target');
          const fillId = ev.dataTransfer?.getData('text/plain');
          if (!fillId) return;
          this.engine.moveFill(fillId, aid)
            .then(() => this.renderFillsByAccount())
            .catch((e) => toast(`Move failed — ${(e as Error).message}`));
        });
      }
      el.appendChild(group);
    }
    if (!any) el.innerHTML = '<div class="pnl-empty">No fills yet</div>';
  }

  private wireFillsByAccount(): void {
    const toggle = this.byId('fillsEditToggle');
    toggle.addEventListener('click', () => {
      this.fillsEdit = !this.fillsEdit;
      toggle.textContent = this.fillsEdit ? 'done' : 'edit';
      toggle.classList.toggle('on', this.fillsEdit);
      this.byId('fillsEditBar').style.display = this.fillsEdit ? 'flex' : 'none';
      void this.renderFillsByAccount();
    });

    // Cross-device story: this exports only the hand-made moves, not the fills themselves.
    this.byId('fillsExport').addEventListener('click', () => {
      const blob = new Blob([this.engine.exportFillOverrides()], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `kladder-fill-overrides-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    });
    this.byId('fillsImport').addEventListener('click', () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'application/json,.json';
      input.addEventListener('change', async () => {
        const file = input.files?.[0];
        if (!file) return;
        try {
          const n = await this.engine.importFillOverrides(await file.text());
          toast(`Applied ${n} reassignment(s).`);
          await this.renderFillsByAccount();
        } catch (e) {
          toast(`Import failed — ${(e as Error).message}`);
        }
      });
      input.click();
    });
  }

  /** Account tree with P&L, and each leaf's positions; the same for every venue. */
  private renderAccounts(el: HTMLElement, msg: PnlSnapshot, venue: 'kalshi' | 'polymarket' = 'kalshi'): void {
    const st = this.listState[venue];
    el.replaceChildren();
    const byId = new Map<string, AccountRow>(msg.accounts.map((a) => [a.id, a]));
    const tree = msg.tree;
    if (!Object.keys(tree.children).length) {
      el.innerHTML = '<div class="pnl-empty">No accounts</div>';
      return;
    }
    if (!st.acctInit) {
      const collect = (node: PnlTreeNode, path: string[]) => {
        for (const nm of Object.keys(node.children)) {
          const child = node.children[nm];
          const parts = [...path, nm];
          st.acct.set(child.id ?? parts.join('/'), true);
          if (!child.id) collect(child, parts);
        }
      };
      collect(tree, []);
      st.acctInit = true;
    }

    const bgOf = (v: number) => (v > 0 ? 'var(--pnl-pos-bg)' : v < 0 ? 'var(--pnl-neg-bg)' : 'var(--hairline)');
    const walk = (node: PnlTreeNode, depth: number, path: string[]) => {
      for (const nm of Object.keys(node.children).sort()) {
        const child = node.children[nm];
        const isLeaf = !!child.id;
        const parts = [...path, nm];
        const key = child.id ?? parts.join('/');
        const collapsed = !!st.acct.get(key);

        const row = document.createElement('div');
        row.className = 'acct-node';
        row.style.padding = `3px 6px 3px ${6 + depth * 12}px`;
        row.style.background = bgOf(child.pnlCash);
        const posTag = child.positionMq !== 0
          ? `<span class="mono" style="opacity:.85;font-variant-numeric:tabular-nums">${child.positionMq > 0 ? '+' : ''}${formatMq(child.positionMq)}</span>`
          : '';
        row.innerHTML =
          `<span><span class="caret${collapsed ? '' : ' open'}"></span> ${escHtml(nm)}</span>`
          + `<span style="display:flex;gap:10px;align-items:center;flex-shrink:0;font-variant-numeric:tabular-nums">${posTag}<span class="mono">${unitHtml(money(child.pnlCash))}</span></span>`;
        row.addEventListener('click', () => {
          st.acct.set(key, !collapsed);
          this.renderAccounts(el, msg, venue);
        });
        el.appendChild(row);
        if (collapsed) continue;
        if (!isLeaf) {
          walk(child, depth + 1, parts);
          continue;
        }
        const acct = byId.get(child.id!);
        const positions = acct?.positions ?? [];
        for (const p of positions) {
          const posCol = p.positionMq > 0 ? 'var(--bid-txt)' : p.positionMq < 0 ? 'var(--ask-txt)' : 'var(--dim1)';
          const prow = document.createElement('div');
          prow.className = 'acct-pos-row';
          prow.style.padding = `2px 6px 2px ${6 + (depth + 1) * 12}px`;
          prow.innerHTML =
            `<span class="js-pos-tk" style="cursor:pointer;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">`
            + `<span style="color:var(--dim1)" title="${escHtml(p.ticker)}">${escHtml(this.labelOf(venue, p.ticker))}</span> `
            + `<span style="color:${posCol};font-weight:600">${p.positionMq > 0 ? '+' : ''}${formatMq(p.positionMq)}</span></span>`
            + `<span style="color:${p.pnlCash > 0 ? 'var(--ok)' : p.pnlCash < 0 ? 'var(--bad)' : 'var(--text)'};font-variant-numeric:tabular-nums;flex-shrink:0">${unitHtml(money(p.pnlCash))}</span>`;
          prow.querySelector('.js-pos-tk')!.addEventListener('click', () => this.openTicker(p.ticker, venue));
          el.appendChild(prow);
        }
        if (!positions.length) {
          const empty = document.createElement('div');
          empty.style.cssText = `padding:2px 6px 2px ${6 + (depth + 1) * 12}px;font-size:10px;color:var(--dim2)`;
          empty.textContent = 'no positions';
          el.appendChild(empty);
        }
      }
    };
    walk(tree, 0, []);
  }

  private renderOrdersOut(el: HTMLElement, msg: PnlSnapshot, venue: 'kalshi' | 'polymarket' = 'kalshi'): void {
    el.replaceChildren();
    if (!msg.orders_out.length) {
      el.innerHTML = '<div class="pnl-empty">No orders out</div>';
      return;
    }
    for (const o of msg.orders_out) {
      const row = document.createElement('div');
      row.className = 'orders-out-row';
      row.innerHTML =
        `<span class="orders-out-tk" title="${escHtml(o.ticker)}">${escHtml(this.labelOf(venue, o.ticker))}`
        + (o.account ? `<span style="font-size:9px;opacity:.6;margin-left:4px">${escHtml(o.account)}</span>` : '')
        + '</span><span class="orders-out-chips">'
        + (o.bids ? `<span class="oc-bid">${o.bids}B</span>` : '')
        + (o.asks ? `<span class="oc-ask">${o.asks}A</span>` : '')
        + (o.bracket ? `<span class="oc-brk" title="bracket order">⧉${o.bracket}</span>` : '')
        + '</span>';
      row.addEventListener('click', () => {
        const target = this.targetPanel(venue);
        if (!target) return;
        // Pre-bind the account these orders are on, so the panel opens scoped to it.
        if (o.account_id && this.accountsList.some((a) => a.id === o.account_id)) {
          target.setAccount(o.account_id);
        }
        target.select(o.ticker);
      });
      el.appendChild(row);
    }
  }

  /** Where a sidebar click opens a contract: a spare ladder, else a new one, else the first. */
  private targetPanel(venue: VenueId = 'kalshi'): Panel | null {
    const spare = this.panels.find((p) => p.venue === venue && !p.ticker);
    if (spare) return spare;
    const added = this.addPanel(undefined, venue);
    if (added) return added;
    toast('No room left in the grid — close or shrink a ladder first.');
    return this.panels.find((p) => p.venue === venue) ?? null;
  }

  private openTicker(tk: string, venue: VenueId = 'kalshi'): void {
    this.targetPanel(venue)?.select(tk);
  }

  private renderStarred(): void {
    const el = this.byId('pnlStarred');
    const arr = getStarred();
    if (!arr.length) {
      el.innerHTML = '<div class="pnl-empty">No starred contracts</div>';
      return;
    }
    el.replaceChildren();
    for (const key of arr) {
      const { venue, ticker: tk } = parseStarKey(key);
      const row = document.createElement('div');
      row.className = 'starred-item';
      row.innerHTML = `<span class="starred-tk">${venue === 'polymarket' ? '<span class="gi-venue" style="margin-right:5px">P</span>' : ''}${escHtml(tk)}</span><button class="starred-rm" title="Unstar">★</button>`;
      row.querySelector('.starred-tk')!.addEventListener('click', () => this.openTicker(tk, venue));
      row.querySelector('.starred-rm')!.addEventListener('click', () => {
        saveStarred(getStarred().filter((t) => t !== key));
        this.renderStarred();
        this.panels.forEach((p) => {
          if (p.venue === venue && p.ticker === tk) p.syncStar();
        });
      });
      el.appendChild(row);
    }
  }
}

/** Colours come from the theme block in styles.css, never from a literal here. */
function pnlColor(cash: number): string {
  return cash > 0 ? 'var(--ok)' : cash < 0 ? 'var(--bad)' : '';
}

const LOG_MIN = 90;
const LOG_MAX_FRAC = 0.7;
function clampHeight(h: number): number {
  return Math.max(LOG_MIN, Math.min(Math.round(window.innerHeight * LOG_MAX_FRAC), Math.round(h)));
}

function clock(ts: number): string {
  const d = new Date(ts);
  return [d.getHours(), d.getMinutes(), d.getSeconds()]
    .map((n) => String(n).padStart(2, '0')).join(':')
    + '.' + String(d.getMilliseconds()).padStart(3, '0');
}

/** A journal entry: summary line, and the full request/response behind a disclosure. */
function journalRow(e: JournalEntry): HTMLElement {
  const row = document.createElement('div');
  row.className = `lp-row lp-j lp-j-${e.kind}${e.bad ? ' lp-bad' : ''}`;
  const t = document.createElement('span'); t.className = 'lp-t'; t.textContent = clock(e.ts);
  const s = document.createElement('span'); s.className = 'lp-s'; s.textContent = e.tag ? `${e.venue} ${e.tag}` : e.venue; s.title = e.tag ?? '';
  if (e.tag) s.dataset.copy = e.tag;
  const m = document.createElement('span'); m.className = 'lp-m';
  if (e.data !== undefined) {
    const det = document.createElement('details');
    const sum = document.createElement('summary'); sum.textContent = e.msg;
    const pre = document.createElement('pre'); pre.textContent = JSON.stringify(e.data, null, 1);
    det.append(sum, pre);
    m.appendChild(det);
  } else {
    m.textContent = e.msg;
  }
  row.append(t, s, m);
  return row;
}

function logRow(l: LogLine): HTMLElement {
  const d = new Date(l.ts);
  const t = [d.getHours(), d.getMinutes(), d.getSeconds()]
    .map((n) => String(n).padStart(2, '0')).join(':')
    + '.' + String(d.getMilliseconds()).padStart(3, '0');
  const row = document.createElement('div');
  row.className = `lp-row lp-${l.level}`;
  row.innerHTML = `<span class="lp-t"></span><span class="lp-s"></span><span class="lp-m"></span>`;
  (row.children[0] as HTMLElement).textContent = t;
  (row.children[1] as HTMLElement).textContent = l.source;
  (row.children[2] as HTMLElement).textContent = l.msg;
  return row;
}

function newPanelId(): string {
  return 'p' + crypto.randomUUID().replace(/-/g, '').slice(0, 12);
}

/** localStorage is user-editable; validate the shape or one bad entry breaks the whole boot. */
function isSavedPanel(v: unknown): v is SavedPanel {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  const r = o.rect as Record<string, unknown> | undefined;
  return typeof o.id === 'string'
    && (o.mode === undefined || o.mode === 'explore' || o.mode === 'ladder')
    && (o.venue === undefined || o.venue === 'kalshi' || o.venue === 'polymarket' || o.venue === 'arb')
    && (o.legs === undefined || (typeof o.legs === 'object' && o.legs !== null))
    && (o.cross === undefined || typeof o.cross === 'boolean')
    && (o.ticker === null || typeof o.ticker === 'string')
    && !!r && ['x', 'y', 'w', 'h'].every((k) => Number.isFinite(r[k]));
}

/** Live unrealised across open positions, plus realised less fees from our trade history. */
function pmPnlOf(a: PmAccount): number {
  let sum = a.positions.reduce((s, p) => s + p.unrealizedCash, 0);
  for (const l of a.ledger.values()) sum += l.realizedCash - l.feeCash;
  return sum;
}

function money(cash: number): string {
  return (cash >= 0 ? '+$' : '-$') + formatCash(Math.abs(cash));
}

function renderSettledRows(el: HTMLElement, rows: { ticker: string; pnlCash: number; settled_time: string }[], emptyMsg: string): void {
  if (!rows.length) {
    el.innerHTML = `<div class="pnl-empty" style="font-size:10px">${emptyMsg}</div>`;
    return;
  }
  el.innerHTML = rows.map((s) => {
    const col = s.pnlCash > 0 ? 'var(--ok)' : s.pnlCash < 0 ? 'var(--bad)' : 'var(--dim1)';
    return '<div class="settled-row">'
      + `<span class="settled-tk" title="${escHtml(s.ticker)}">${escHtml(s.ticker)}</span>`
      + (s.settled_time ? `<span class="settled-date">${escHtml(s.settled_time)}</span>` : '')
      + `<span class="settled-pnl" style="color:${col}">${unitHtml(money(s.pnlCash))}</span></div>`;
  }).join('');
}

/** Kalshi stars are bare tickers (as saved for years); other venues are prefixed. */
function starKey(venue: VenueId, ticker: string): string {
  return venue === 'kalshi' ? ticker : `${venue}:${ticker}`;
}

function parseStarKey(key: string): { venue: VenueId; ticker: string } {
  const m = key.match(/^(polymarket):(.+)$/);
  return m ? { venue: m[1] as VenueId, ticker: m[2] } : { venue: 'kalshi', ticker: key };
}

function getStarred(): string[] {
  try {
    return JSON.parse(localStorage.getItem('kalshi_starred') || '[]');
  } catch {
    return [];
  }
}

function saveStarred(arr: string[]): void {
  localStorage.setItem('kalshi_starred', JSON.stringify(arr));
}

function toggleStar(ticker: string): void {
  const arr = getStarred();
  const idx = arr.indexOf(ticker);
  if (idx >= 0) arr.splice(idx, 1);
  else arr.push(ticker);
  saveStarred(arr);
}
